import { useEffect, useState, useRef } from 'react';
import { useStore } from '../store/useStore';

export function CustomCursor() {
  const cursorSettings = useStore((s) => s.cursorSettings);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [trail, setTrail] = useState<{ x: number; y: number; id: number }[]>([]);
  const [sparkles, setSparkles] = useState<{ x: number; y: number; id: number }[]>([]);
  const cursorRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const trailId = useRef(0);
  const sparkleId = useRef(0);

  useEffect(() => {
    // Don't register any listeners if cursor is disabled
    if (!cursorSettings.enabled || cursorSettings.type === 'none') return;

    const onMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });
      setIsVisible(true);

      // Check if hovering over interactive elements
      const target = e.target as HTMLElement;
      const isInteractive = target?.closest('button, a, input, select, textarea, [role="button"], .cursor-pointer');
      setIsHovering(!!isInteractive);

      // Trail effect: add point to trail
      if (cursorSettings.type === 'trail') {
        trailId.current++;
        setTrail((prev) => [...prev.slice(-12), { x: e.clientX, y: e.clientY, id: trailId.current }]);
      }

      // Sparkles effect: randomly add sparkle
      if (cursorSettings.type === 'sparkles' && Math.random() > 0.6) {
        sparkleId.current++;
        const offset = { x: (Math.random() - 0.5) * 40, y: (Math.random() - 0.5) * 40 };
        setSparkles((prev) => [
          ...prev.slice(-8),
          { x: e.clientX + offset.x, y: e.clientY + offset.y, id: sparkleId.current },
        ]);
      }
    };

    const onMouseLeave = () => setIsVisible(false);
    const onMouseEnter = () => setIsVisible(true);

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('mouseenter', onMouseEnter);

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('mouseenter', onMouseEnter);
    };
  }, [cursorSettings.enabled, cursorSettings.type]);

  // Auto-clean trail/sparkle particles
  useEffect(() => {
    if (cursorSettings.type !== 'trail' && cursorSettings.type !== 'sparkles') return;
    const interval = setInterval(() => {
      setTrail((prev) => prev.slice(1));
      setSparkles((prev) => prev.slice(1));
    }, 80);
    return () => clearInterval(interval);
  }, [cursorSettings.type]);

  // Don't render anything if disabled
  if (!cursorSettings.enabled || cursorSettings.type === 'none') return null;
  if (!isVisible) return null;

  const intensity = (cursorSettings.intensity || 50) / 100;
  const size = cursorSettings.size || 20;

  // ── GLOW EFFECT ─────────────────────────────────────────────────────
  if (cursorSettings.type === 'glow') {
    return (
      <>
        {/* Outer Glow / Halo */}
        <div
          ref={glowRef}
          className="fixed pointer-events-none z-[99999] transition-transform duration-300 ease-out"
          style={{
            left: position.x,
            top: position.y,
            width: `${size * 7}px`,
            height: `${size * 7}px`,
            background: `radial-gradient(circle, var(--t-primary) 0%, transparent 70%)`,
            opacity: isHovering ? intensity * 0.3 : intensity * 0.15,
            transform: `translate(-50%, -50%) scale(${isHovering ? 1.5 : 1})`,
            filter: 'blur(20px)',
          }}
        />
        {/* Main Cursor Dot */}
        <div
          ref={cursorRef}
          className="fixed pointer-events-none z-[99999] mix-blend-difference"
          style={{
            left: position.x,
            top: position.y,
            width: isHovering ? '40px' : `${size * 0.4}px`,
            height: isHovering ? '40px' : `${size * 0.4}px`,
            backgroundColor: 'white',
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            transition: 'width 0.2s, height 0.2s',
            opacity: 0.8 * intensity,
          }}
        />
      </>
    );
  }

  // ── SPOTLIGHT EFFECT ────────────────────────────────────────────────
  if (cursorSettings.type === 'spotlight') {
    return (
      <div
        className="fixed pointer-events-none z-[99999] transition-all duration-200 ease-out"
        style={{
          left: position.x,
          top: position.y,
          width: `${size * 10}px`,
          height: `${size * 10}px`,
          background: `radial-gradient(circle, transparent 30%, rgba(0,0,0,${0.4 * intensity}) 100%)`,
          transform: `translate(-50%, -50%) scale(${isHovering ? 1.2 : 1})`,
          transition: 'transform 0.3s, opacity 0.3s',
          mixBlendMode: 'multiply',
        }}
      />
    );
  }

  // ── TRAIL EFFECT ────────────────────────────────────────────────────
  if (cursorSettings.type === 'trail') {
    return (
      <>
        {trail.map((point, i) => {
          const scale = (i + 1) / trail.length;
          return (
            <div
              key={point.id}
              className="fixed pointer-events-none z-[99999]"
              style={{
                left: point.x,
                top: point.y,
                width: `${size * 0.3 * scale}px`,
                height: `${size * 0.3 * scale}px`,
                backgroundColor: 'var(--t-primary)',
                borderRadius: '50%',
                transform: 'translate(-50%, -50%)',
                opacity: scale * intensity * 0.6,
                transition: 'opacity 0.15s',
              }}
            />
          );
        })}
        {/* Main dot */}
        <div
          className="fixed pointer-events-none z-[99999]"
          style={{
            left: position.x,
            top: position.y,
            width: isHovering ? '16px' : `${size * 0.4}px`,
            height: isHovering ? '16px' : `${size * 0.4}px`,
            backgroundColor: 'var(--t-primary)',
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            opacity: intensity,
            transition: 'width 0.15s, height 0.15s',
          }}
        />
      </>
    );
  }

  // ── SPARKLES EFFECT ─────────────────────────────────────────────────
  if (cursorSettings.type === 'sparkles') {
    return (
      <>
        {sparkles.map((s) => (
          <div
            key={s.id}
            className="fixed pointer-events-none z-[99999] animate-ping"
            style={{
              left: s.x,
              top: s.y,
              width: `${4 + Math.random() * 4}px`,
              height: `${4 + Math.random() * 4}px`,
              backgroundColor: 'var(--t-primary)',
              borderRadius: '50%',
              transform: 'translate(-50%, -50%)',
              opacity: intensity * 0.7,
              animationDuration: '0.6s',
            }}
          />
        ))}
        {/* Main dot */}
        <div
          className="fixed pointer-events-none z-[99999]"
          style={{
            left: position.x,
            top: position.y,
            width: isHovering ? '20px' : `${size * 0.35}px`,
            height: isHovering ? '20px' : `${size * 0.35}px`,
            backgroundColor: 'white',
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            opacity: intensity * 0.9,
            transition: 'width 0.2s, height 0.2s',
            mixBlendMode: 'difference',
          }}
        />
      </>
    );
  }

  return null;
}
